import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  inject,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectClearDirective } from '../shared/ss-select-clear.directive';
import * as echarts from 'echarts';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

import { SelfServiceChartService } from '../shared/self-service-chart.service';
import { OwnedDashboardLayout, SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { SelfServiceStateService } from '../shared/self-service-state.service';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { SelfServiceDataUtilsService } from '../shared/self-service-data-utils.service';
import { readDynamicDashboardCache, readJson } from '../shared/self-service-storage.utils';
import {
  ChartOptionContext,
  SelfServiceChartOptionBuilderService,
} from '../shared/self-service-chart-option-builder.service';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceApiConfigService } from '../shared/self-service-api-config.service';
import {
  BuilderLayoutConfig,
  BuilderStep,
} from '../shared/self-service-config.types';

import {
  ActiveCrossFilter,
  AggregationType,
  ChartPreviewKind,
  ChartShapeOptions,
  ChartStyle,
  ChartTypeOption,
  DashboardCard,
  DashboardDraftItem,
  DashboardDraftState,
  DashboardStatus,
  DashboardTab,
  DatasetColumn,
  GlobalFilter,
  GlobalFiltersPosition,
  SavedSelfServiceChart,
  SavedSelfServiceDashboard,
  UploadedDataset,
  WidgetConfig,
  isWidgetPreviewKind,
} from '../shared/self-service-types';
import { SelfServiceWidgetComponent } from '../shared/self-service-widget.component';
import { GenericDrillDownComponent } from '../../chart-components/generic-drill-down/generic-drill-down.component';
import { OnTheFlyComputationComponent, OtfAppliedConfig } from './on-the-fly-computation/on-the-fly-computation.component';

/**
 * Dashboard-builder stage extracted from SelfServiceVisualizationComponent.
 *
 * Owns the dashboard grid / cross-filter / global-filter / publish cluster. The
 * dashboard draft + saved dashboards + global filter selections live in
 * SelfServiceStateService (shared cross-stage signal state); this component
 * reads/writes them through getter/setter delegation so the moved template
 * bindings keep working unchanged.
 *
 * The grid renders each ECharts card directly into a #dashboardChart <div>,
 * keeping its own Map<itemId, echarts.ECharts>. It re-renders on
 * ResponsiveLayoutService.reflow$ (window resize / sidebar toggle) following the
 * project's responsive pattern.
 *
 * Cross-stage actions it cannot own (editing a saved chart in the Chart Builder,
 * switching the active view) are raised as output() events for the shell.
 */
@Component({
  selector: 'app-dashboard-builder',
  standalone: true,
  // Default change detection (not OnPush): dashboard rendering, cross-filters,
  // save/publish all run in async callbacks and read signal-getters; OnPush would
  // leave the grid/controls stale. Matches the original monolith's default CD.
  imports: [
    CommonModule,
    FormsModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    SsSelectClearDirective,
    SelfServiceWidgetComponent,
    OnTheFlyComputationComponent,
    GenericDrillDownComponent,
  ],
  templateUrl: './dashboard-builder.component.html',
  styleUrl: './dashboard-builder.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DashboardBuilderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dashboardFilterLabelInput', { static: false })
  dashboardFilterLabelInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dashboardNameInput', { static: false })
  dashboardNameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dashboardStepper', { static: false })
  dashboardStepper?: MatStepper;
  @ViewChildren('dashboardChart')
  dashboardChartRefs?: QueryList<ElementRef<HTMLDivElement>>;
  // Publish loading veil — reparented to <body> in ngAfterViewInit (see there)
  // so `position: fixed` centers on the real viewport instead of whatever
  // transformed/scrolling ancestor it would otherwise inherit as its
  // containing block, which was leaving it off-screen when the page was
  // scrolled down.
  @ViewChild('publishOverlay', { static: false })
  publishOverlayRef?: ElementRef<HTMLElement>;

  // ── Shared services ──────────────────────────────────────────────────────
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  protected readonly state = inject(SelfServiceStateService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly chartService = inject(SelfServiceChartService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly dataUtils = inject(SelfServiceDataUtilsService);
  private readonly chartOptionBuilder = inject(SelfServiceChartOptionBuilderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly keycloak = inject(KeycloakService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly apiConfig = inject(SelfServiceApiConfigService);
  private readonly http = inject(HttpClient);

  // ── JSON-driven layout (see JSON_CONFIG_PLAN.md) ─────────────────────────
  /** Registry of step-body template ids this template exposes. */
  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>([
    'setup',
    'components',
    'filters',
    'arrange',
  ]);

  /** Built-in default layout — identical to the original 4-step stepper. Used
   *  when no `dashboard-builder.json` is present so the builder renders unchanged. */
  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'dashboard',
    stepperOrientation: 'horizontal',
    linear: false,
    steps: [
      { id: 'setup', label: 'Setup', sections: [{ id: 'setup', fields: [{ field: 'setup' }] }] },
      { id: 'components', label: 'Components', sections: [{ id: 'components', fields: [{ field: 'components' }] }] },
      { id: 'filters', label: 'Filters', sections: [{ id: 'filters', fields: [{ field: 'filters' }] }] },
      { id: 'arrange', label: 'Arrange', sections: [{ id: 'arrange', fields: [{ field: 'arrange' }] }] },
    ],
  };

  /** Active layout (default until the JSON config resolves in ngOnInit). */
  layout: BuilderLayoutConfig = DashboardBuilderComponent.DEFAULT_LAYOUT;

  // ── Events the shell handles (cross-stage actions the dashboard requests
  //    but does not own). ────────────────────────────────────────────────────
  /** Ask the shell to load a saved chart into the Chart Builder. */
  readonly editChart = output<SavedSelfServiceChart>();
  /** Ask the shell to switch the active view (e.g. to 'charts'). */
  readonly viewModeChange = output<'data' | 'charts' | 'dashboards'>();
  /** Ask the shell to open Chart Builder as a fresh chart flow. */
  readonly startNewChartRequest = output<void>();

  otfDrawerOpen = false;
  otfCardChart: SavedSelfServiceChart | null = null;
  /** Snapshot of the card's rows before OTF mutations — used for restoring. */
  otfOriginalRows: Record<string, string>[] = [];

  openCardOtf(card: DashboardCard, event: MouseEvent): void {
    event.stopPropagation();
    // Store the original rows before any OTF manipulation so we can restore later
    this.otfCardChart = card.chart;
    this.otfOriginalRows = [...(card.chart.dataset.rows ?? [])];
    this.otfDrawerOpen = true;
  }

  /**
   * Receives client-side computed rows from the OTF drawer and updates the
   * active card's dataset in-place so the chart re-renders with the new values.
   */
  onOtfAppliedRows(rows: Record<string, unknown>[]): void {
    if (!this.otfCardChart) return;
    this.applyOtfRowsToDashboard(rows);
  }

  onOtfDataResult(result: Record<string, unknown> | Record<string, unknown>[]): void {
    this.applyOtfRowsToDashboard(this.normalizeOtfRows(result));
  }

  onOtfComputationApplied(config: OtfAppliedConfig): void {
    if (!this.otfCardChart) return;
    this.saveOtfConfigForChart(this.otfCardChart.id, config);
  }

  onWidgetValueChange(card: DashboardCard, value: Record<string, unknown>): void {
    const otfConfig = card.chart.config?.['onTheFlyComputation'] as OtfAppliedConfig | undefined;
    if (!otfConfig?.expressions?.length || !otfConfig.entityName) return;

    const context = {
      sliderValue: value['sliderValue'] ?? value['widgetValue'] ?? otfConfig.sliderValue,
      inputValue: value['inputValue'] ?? value['widgetValue'] ?? otfConfig.sliderValue,
      widgetValue: value['widgetValue'] ?? value['sliderValue'] ?? value['inputValue'] ?? otfConfig.sliderValue,
      rangeStart: value['rangeStart'],
      rangeEnd: value['rangeEnd'],
      ...value,
    };

    const resolveTokens = (expr: string) => Object.entries(context).reduce(
      (next, [key, raw]) => next.replace(new RegExp(`\\b${key}\\b`, 'g'), String(raw ?? '')),
      expr,
    );

    this.http
      .post<Record<string, unknown> | Record<string, unknown>[]>(
        `${String(environment.URL || '').replace(/\/$/, '')}/getData`,
        {
          entityName: otfConfig.entityName,
          attributesList: otfConfig.expressions.map((expr) => resolveTokens(expr.raw)),
          filterList: this.buildOtfFilterList(),
        },
      )
      .subscribe({
        next: (result) => this.applyOtfRowsToDashboard(this.normalizeOtfRows(result)),
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.errorMessage = error?.error?.message ?? error?.message ?? 'Failed to run on-the-fly computation.';
        },
      });
  }

  private normalizeOtfRows(result: Record<string, unknown> | Record<string, unknown>[]): Record<string, unknown>[] {
    if (Array.isArray(result)) return result;
    const values = Object.values(result ?? {});
    const nestedRows = values.find((value): value is Record<string, unknown>[] => Array.isArray(value));
    return nestedRows ?? (result ? [result] : []);
  }

  private applyOtfRowsToDashboard(rows: Record<string, unknown>[]): void {
    if (!rows.length) return;
    const computedKeys = new Set(rows.flatMap((row) => Object.keys(row)));

    const nextCharts = this.savedCharts.map((chart) => {
      const chartUsesComputedKey = this.chartUsesAnyColumn(chart, computedKeys);
      const isSourceChart = !!this.otfCardChart && chart.id === this.otfCardChart.id;
      if (!chartUsesComputedKey && !isSourceChart) return chart;

      const baseRows = chart.dataset.rows ?? [];
      if (baseRows.length && baseRows.length !== rows.length) return chart;

      const mergedRows = (baseRows.length ? baseRows : rows).map((row, index) => ({
        ...row,
        ...(rows[index] ?? {}),
      })) as Record<string, string>[];

      const existingColumns = new Set(chart.dataset.columns.map((col) => col.name));
      const addedColumns = [...computedKeys]
        .filter((key) => !existingColumns.has(key))
        .map((name) => ({ name, type: 'number' as const }));

      return {
        ...chart,
        dataset: {
          ...chart.dataset,
          columns: [...chart.dataset.columns, ...addedColumns],
          rows: mergedRows,
        },
      };
    });

    this.savedCharts = nextCharts;
    this.filterOptionsCache.clear();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  private chartUsesAnyColumn(chart: SavedSelfServiceChart, keys: Set<string>): boolean {
    const mappedColumns = Object.values(chart.mapping ?? {}).filter((value): value is string => !!value);
    const datasetColumns = chart.dataset.columns.map((column) => column.name);
    return [...mappedColumns, ...datasetColumns].some((column) => keys.has(column));
  }

  private saveOtfConfigForChart(chartId: string, config: OtfAppliedConfig): void {
    const nextCharts = this.savedCharts.map((chart) => (
      chart.id === chartId
        ? { ...chart, config: { ...chart.config, onTheFlyComputation: config } }
        : chart
    ));
    this.savedCharts = nextCharts;
    this.persistence.persistSavedCharts(nextCharts);
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
  }

  private buildOtfFilterList(): Array<Record<string, string>> {
    const entries = Object.entries(this.activeGlobalFilters)
      .flatMap(([key, values]) => (values ?? []).map((value) => ({ key, value })));
    return entries.map(({ key, value }, index) => ({
      operator: '=',
      attributeName: key,
      attributeValue: String(value),
      logicalOperator: index < entries.length - 1 ? 'AND' : '',
    }));
  }
  private static readonly DASHBOARD_DRAFT_STORAGE_KEY = 'dvm.selfService.dashboardDraft';
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  private readonly dashboardDraftSave$ = new Subject<DashboardDraftState>();
  private dashboardDraftSaveSub?: Subscription;
  private reflowSub?: Subscription;
  /** In-flight fetch of the current user's published dashboards from the API
   *  (same call the sidenav uses) to reconcile into the Saved dashboards tab. */
  private ownedDashboardsSub?: Subscription;
  /** In-flight "Back to Builder" API restore of a published dashboard by slug. */
  private restoreFromApiSub?: Subscription;
  /** Slugs of dashboards that exist ONLY on the server (API) with no editable
   *  local copy. Clicking one opens the published view instead of the builder,
   *  because the API layout_json doesn't carry editable builder items. */
  private apiOnlyDashboardSlugs = new Set<string>();

  readonly builtinAggregations: Array<{ label: string; value: AggregationType }> = [
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Distinct Count', value: 'distinctCount' },
    { label: 'Distinct Values', value: 'distinct' },
    { label: 'Minimum', value: 'min' },
    { label: 'Maximum', value: 'max' },
    { label: 'Median', value: 'median' },
    { label: 'Range (max - min)', value: 'range' },
    { label: 'First', value: 'first' },
    { label: 'Last', value: 'last' }
  ];

  // ── Template-facing accessors for shared signal state ───────────────────────
  // The moved template binds bare names (dashboardName, dashboardItems, ...);
  // these getters/setters delegate to SelfServiceStateService.
  get dashboardName(): string { return this.state.dashboardName(); }
  set dashboardName(v: string) { this.state.setDashboardName(v); }
  get savedCharts(): SavedSelfServiceChart[] { return this.state.savedCharts(); }
  set savedCharts(v: SavedSelfServiceChart[]) { this.state.setSavedCharts(v); }
  get savedDashboards(): SavedSelfServiceDashboard[] { return this.state.savedDashboards(); }
  set savedDashboards(v: SavedSelfServiceDashboard[]) { this.state.setSavedDashboards(v); }
  get dashboardItems(): DashboardDraftItem[] { return this.state.dashboardItems(); }
  set dashboardItems(v: DashboardDraftItem[]) { this.state.setDashboardItems(v); }
  get dashboardGlobalFilters(): GlobalFilter[] { return this.state.dashboardGlobalFilters(); }
  set dashboardGlobalFilters(v: GlobalFilter[]) { this.state.setDashboardGlobalFilters(v); }
  get dashboardColumns(): number { return this.state.dashboardColumns(); }
  set dashboardColumns(v: number) { this.state.setDashboardColumns(v); }
  get dashboardGlobalFiltersPosition(): GlobalFiltersPosition { return this.state.dashboardGlobalFiltersPosition(); }
  set dashboardGlobalFiltersPosition(v: GlobalFiltersPosition) { this.state.setDashboardGlobalFiltersPosition(v); }
  get dashboardStatus(): DashboardStatus { return this.state.dashboardStatus(); }
  set dashboardStatus(v: DashboardStatus) { this.state.setDashboardStatus(v); }
  get dashboardPublishedAt(): string | null { return this.state.dashboardPublishedAt(); }
  set dashboardPublishedAt(v: string | null) { this.state.setDashboardPublishedAt(v); }
  get activeGlobalFilters(): Record<string, string[]> { return this.state.activeGlobalFilters(); }
  set activeGlobalFilters(v: Record<string, string[]>) { this.state.setActiveGlobalFilters(v); }
  get activeSavedDashboardId(): string { return this.state.activeSavedDashboardId(); }
  set activeSavedDashboardId(v: string) { this.state.setActiveSavedDashboardId(v); }
  get dashboardTabs(): DashboardTab[] { return this.state.dashboardTabs(); }
  set dashboardTabs(v: DashboardTab[]) { this.state.setDashboardTabs(v); }
  get activeBuilderTabId(): string | null { return this.state.activeBuilderTabId(); }
  set activeBuilderTabId(v: string | null) { this.state.setActiveBuilderTabId(v); }
  private get chartTypes(): ChartTypeOption[] { return this.state.chartTypes(); }

  /** True when the dashboard is in tabbed mode (one or more tabs defined). */
  get useTabs(): boolean { return this.dashboardTabs.length > 0; }

  /** Label of the tab currently being edited — used in the repository card's
   *  Add/Remove tooltips so the user sees exactly which tab they act on. */
  get activeBuilderTabLabel(): string {
    const activeId = this.activeBuilderTabId;
    return this.dashboardTabs.find((tab) => tab.id === activeId)?.label ?? '';
  }

  /** Charts belonging to the active tab (or all charts in single-page mode).
   *  Drives which cards the Components/Arrange steps show. */
  get visibleDashboardItems(): DashboardDraftItem[] {
    if (!this.useTabs) return this.dashboardItems;
    const activeId = this.activeBuilderTabId;
    return this.dashboardItems.filter((item) => item.tabId === activeId);
  }

  showSavedDashboardPicker = true;
  private readonly globalFiltersPositionOptions_DEFAULT: Array<{ label: string; value: GlobalFiltersPosition }> = [
    { label: 'Top', value: 'top' },
    { label: 'Left', value: 'left' },
    { label: 'Right', value: 'right' },
  ];
  /** Global-filters placement choices (JSON-configurable via options.globalFiltersPositions). */
  get globalFiltersPositionOptions(): Array<{ label: string; value: GlobalFiltersPosition }> {
    return (
      (this.layout.options?.globalFiltersPositions as Array<{ label: string; value: GlobalFiltersPosition }> | undefined) ??
      this.globalFiltersPositionOptions_DEFAULT
    );
  }
  newGlobalFilterLabel = '';
  newGlobalFilterKey = '';
  newGlobalFilterMultiSelect = true;
  newGlobalFilterParentKey = '';
  newGlobalFilterError = '';
  generatedDashboardConfig = '';
  private readonly columnSpanOptions_DEFAULT: Array<{ value: number; label: string }> = [
    { value: 1, label: '1 col' },
    { value: 2, label: '2 cols' },
    { value: 3, label: '3 cols' },
    { value: 4, label: 'Full width' },
  ];
  /** Per-item span choices (JSON-configurable via options.columnSpanOptions).
   *  JSON values arrive as strings; coerce `value` back to number. */
  get columnSpanOptions(): Array<{ value: number; label: string }> {
    const configured = this.layout.options?.columnSpanOptions;
    if (Array.isArray(configured) && configured.length) {
      return configured.map((o) => ({ value: Number((o as { value: unknown }).value), label: String(o.label) }));
    }
    return this.columnSpanOptions_DEFAULT;
  }
  private readonly dashboardColumnOptions_DEFAULT: number[] = [1, 2, 3, 4];
  /** Grid-column choices (JSON-configurable via options.columnOptions). */
  get dashboardColumnOptions(): number[] {
    return this.layout.options?.columnOptions ?? this.dashboardColumnOptions_DEFAULT;
  }
  expandedListenerEditor: string | null = null;

  // Keep local message fields because the monolith bound/relayed them; mirror
  // the side-effect through the shared UI service (snackbar feedback).
  private _errorMessage = '';
  get errorMessage(): string { return this._errorMessage; }
  set errorMessage(val: string) {
    this._errorMessage = val;
    this.ui.showError(val);
  }

  private _saveMessage = '';
  get saveMessage(): string { return this._saveMessage; }
  set saveMessage(val: string) {
    this._saveMessage = val;
    this.ui.showSuccess(val);
  }

  validationMessage = '';

  /** Which save/publish/unpublish action is in flight, if any — drives the
   *  loading spinner + disabled state on the Save/Update/Publish buttons so
   *  a slow save request doesn't look like a no-op click. */
  dashboardActionPending: 'save' | 'publish' | 'unpublish' | null = null;

  // ng-select v15 disables options via a `disabled` field on the item itself
  // (the older [isOptionDisabled] callback was removed). Cached per dashboardColumns
  // value so we don't allocate a new array on every change-detection cycle.
  private spanOptionsCache?: { columns: number; options: Array<{ value: number; label: string; disabled: boolean }> };
  get spanOptionsForCurrentGrid(): Array<{ value: number; label: string; disabled: boolean }> {
    if (this.spanOptionsCache && this.spanOptionsCache.columns === this.dashboardColumns) {
      return this.spanOptionsCache.options;
    }
    const options = this.columnSpanOptions.map((o) => ({ ...o, disabled: o.value > this.dashboardColumns }));
    this.spanOptionsCache = { columns: this.dashboardColumns, options };
    return options;
  }

  private currentShapeOptions: ChartShapeOptions | null = null;

  private dashboardCharts = new Map<string, echarts.ECharts>();
  private dashboardRenderTimer?: ReturnType<typeof setTimeout>;
  private activeCrossFilters = new Map<string, ActiveCrossFilter>();

  private filterOptionsCache = new Map<string, string[]>();
  private filterValueCache = new Map<string, string[]>();
  private static readonly EMPTY_STRING_ARRAY: string[] = [];

  // ── Getters bound by the template ────────────────────────────────────────
  get canSaveDashboard(): boolean {
    return !!this.dashboardName.trim() && this.dashboardItems.some((item) => !!this.persistence.resolveDashboardChart(item));
  }

  get hasActiveDashboardDraft(): boolean {
    return (
      !!this.activeSavedDashboardId
      || this.dashboardItems.length > 0
      || this.dashboardGlobalFilters.length > 0
      || this.normalizeDashboardName(this.dashboardName) !== this.normalizeDashboardName('Untitled dashboard')
    );
  }

  get dashboardCards(): DashboardCard[] {
    // In tabbed mode, only the active tab's charts are shown/rendered in the
    // builder preview; single-page mode shows everything (visibleDashboardItems
    // returns all items when there are no tabs).
    return this.visibleDashboardItems.reduce<DashboardCard[]>((cards, item) => {
      const chart = this.persistence.resolveDashboardChart(item);
      if (chart) {
        cards.push({ item, chart });
      }
      return cards;
    }, []);
  }

  get hasActiveGlobalFilters(): boolean {
    return Object.values(this.activeGlobalFilters).some((arr) => arr && arr.length > 0);
  }

  get sortedDashboardGlobalFilters(): GlobalFilter[] {
    const sorted: GlobalFilter[] = [];
    const added = new Set<string>();
    const remaining = [...this.dashboardGlobalFilters];
    let safety = remaining.length + 1;
    while (remaining.length && safety-- > 0) {
      const i = remaining.findIndex((f) => !f.parentKey || added.has(f.parentKey));
      if (i === -1) break;
      const [picked] = remaining.splice(i, 1);
      sorted.push(picked);
      added.add(picked.key);
    }
    return [...sorted, ...remaining];
  }

  get hasActiveCrossFilters(): boolean {
    return this.activeCrossFilters.size > 0;
  }

  get eChartsDashboardCards(): DashboardCard[] {
    return this.dashboardCards.filter((card) => this.isEChartsCard(card));
  }

  ngOnInit(): void {
    this.loadSavedDashboards();
    this.loadDashboardDraft();
    this.restoreDashboardBuilderReturnTarget();
    this.dashboardDraftSaveSub = this.dashboardDraftSave$
      .pipe(debounceTime(500))
      .subscribe((draft) => {
        this.dashboardService.saveDraft(draft).subscribe({
          error: () => {
            this.errorMessage = 'Unable to save dashboard draft.';
          },
        });
      });
    this.updateDashboardConfig();

    // Load the JSON layout; falls back to DEFAULT_LAYOUT on any failure.
    this.configService
      .loadBuilderLayout('dashboard', DashboardBuilderComponent.DEFAULT_LAYOUT)
      .subscribe((layout) => {
        const validation = this.configService.validateLayout(
          layout,
          DashboardBuilderComponent.STEP_FIELD_IDS,
        );
        if (validation.unknownFields.length) {
          console.warn(
            '[dashboard-builder] config references unknown field ids; they will be skipped:',
            validation.unknownFields,
          );
        }
        this.layout = layout;
        this.apiConfig.applyFromLayout(layout);
        this.applyConfigDefaults(layout);
      });
  }

  /** Apply JSON `defaults` for a fresh dashboard only — never clobber a loaded
   *  draft. "Fresh" = no items yet and the name is still the untouched default. */
  private applyConfigDefaults(layout: BuilderLayoutConfig): void {
    const defaults = layout.defaults;
    if (!defaults) return;
    const isFresh =
      this.dashboardItems.length === 0 &&
      this.normalizeDashboardName(this.dashboardName) === this.normalizeDashboardName('Untitled dashboard');
    if (!isFresh) return;
    if (typeof defaults.columns === 'number') {
      this.state.setDashboardColumns(defaults.columns);
    }
    if (typeof defaults.dashboardName === 'string' && defaults.dashboardName.trim()) {
      this.state.setDashboardName(defaults.dashboardName);
    }
  }

  // ── Layout helpers (template-facing) ─────────────────────────────────────
  // Step bodies stay inline as direct <mat-step> content; the template asks
  // these helpers, per known step id, whether to render the step and its label.
  // The JSON layout drives visibility + labels.
  // NOTE: onDashboardStepChange uses the step's selectedIndex; hiding a step via
  // config shifts those indices. The default layout keeps all four steps in the
  // original order, so index-based step logic is unaffected by default.

  /** Find the configured step entry carrying a given field id. */
  private stepFor(fieldId: string): BuilderStep | undefined {
    return (this.layout.steps ?? []).find((step) =>
      (step.sections ?? []).some((section) =>
        (section.fields ?? []).some((field) => field.field === fieldId),
      ),
    );
  }

  /** Whether the step carrying `fieldId` should render. Absent from config → hidden. */
  isStepVisible(fieldId: string): boolean {
    const step = this.stepFor(fieldId);
    if (!step) return false;
    return step.visible !== false && this.resolveVisibility(step.visibleWhen);
  }

  /** Label for the step carrying `fieldId`, from config (falls back to the id). */
  stepLabel(fieldId: string): string {
    return this.stepFor(fieldId)?.label ?? fieldId;
  }

  /** Resolve a `visibleWhen` predicate key; unknown/absent keys default visible. */
  private resolveVisibility(key: string | undefined): boolean {
    if (!key) return true;
    const predicates: Record<string, () => boolean> = {
      hasSavedCharts: () => this.savedCharts.length > 0,
    };
    const predicate = predicates[key];
    return predicate ? predicate() : true;
  }

  ngAfterViewInit(): void {
    // Move the publish overlay to <body> once, up front — it's always in the
    // DOM (visibility toggled by the `.active` class in the template) so this
    // only runs the one time, escaping any ancestor's scroll/transform context.
    if (this.publishOverlayRef) {
      this.renderer.appendChild(this.document.body, this.publishOverlayRef.nativeElement);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.dashboardCharts.forEach((chart) => chart.resize());
    });
    this.dashboardChartRefs?.changes.subscribe(() => {
      this.scheduleDashboardRender();
    });
    this.scheduleDashboardRender();

    // Honor a one-shot "open on this step" intent set by the shell (e.g. the
    // chart-builder's "Add to dashboard" flow asks for Setup). This runs after
    // ngOnInit's own step navigation, so it has the final say. Consume + clear.
    if (this.state.pendingDashboardStep() === 'setup') {
      this.state.setPendingDashboardStep(null);
      this.showDashboardSetupStep();
    }
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.dashboardDraftSaveSub?.unsubscribe();
    this.ownedDashboardsSub?.unsubscribe();
    this.restoreFromApiSub?.unsubscribe();
    if (this.dashboardRenderTimer) {
      clearTimeout(this.dashboardRenderTimer);
    }
    this.dashboardCharts.forEach((chart) => chart.dispose());
    this.dashboardCharts.clear();
    // Undo the body reparenting from ngAfterViewInit so the node doesn't leak
    // once this component (and its Angular bindings) are torn down.
    this.publishOverlayRef?.nativeElement.remove();
  }

  // ── Global filters ─────────────────────────────────────────────────────────
  addGlobalFilter(): void {
    const label = this.newGlobalFilterLabel.trim();
    const key = this.newGlobalFilterKey.trim();
    const parentKey = (this.newGlobalFilterParentKey || '').trim();
    this.newGlobalFilterError = '';

    if (!label) { this.newGlobalFilterError = 'Label is required.'; return; }
    if (!key) { this.newGlobalFilterError = 'Column key is required.'; return; }
    if (this.dashboardGlobalFilters.some((f) => f.key === key)) {
      this.newGlobalFilterError = `A global filter for "${key}" already exists.`;
      return;
    }
    if (parentKey && parentKey === key) {
      this.newGlobalFilterError = `A filter cannot be its own parent.`;
      return;
    }
    if (parentKey && !this.dashboardGlobalFilters.some((f) => f.key === parentKey)) {
      this.newGlobalFilterError = `Parent filter "${parentKey}" does not exist.`;
      return;
    }

    this.dashboardGlobalFilters = [
      ...this.dashboardGlobalFilters,
      {
        label,
        key,
        isMultiSelect: this.newGlobalFilterMultiSelect,
        parentKey: parentKey || undefined,
      },
    ];
    this.newGlobalFilterLabel = '';
    this.newGlobalFilterKey = '';
    this.newGlobalFilterParentKey = '';
    this.markDraft();
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  removeGlobalFilter(key: string): void {
    this.dashboardGlobalFilters = this.dashboardGlobalFilters
      .filter((f) => f.key !== key)
      .map((f) => (f.parentKey === key ? { ...f, parentKey: undefined } : f));
    delete this.activeGlobalFilters[key];
    this.markDraft();
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  onGlobalFilterChange(filter: GlobalFilter, value: string | string[]): void {
    const next = Array.isArray(value) ? value : (value ? [value] : []);
    this.activeGlobalFilters = { ...this.activeGlobalFilters, [filter.key]: next };
    this.invalidateChildFilterSelections(filter.key);
    this.scheduleDashboardRender();
  }

  clearAllGlobalFilters(): void {
    this.activeGlobalFilters = {};
    this.scheduleDashboardRender();
  }

  getGlobalFilterOptions(filter: GlobalFilter): string[] {
    const computed = this.computeGlobalFilterOptions(filter);
    const cached = this.filterOptionsCache.get(filter.key);
    if (cached && cached.length === computed.length && cached.every((v, i) => v === computed[i])) {
      return cached;
    }
    this.filterOptionsCache.set(filter.key, computed);
    return computed;
  }

  getActiveFilterValue(filter: GlobalFilter): string[] {
    const current = this.activeGlobalFilters[filter.key];
    if (!current || current.length === 0) {
      return DashboardBuilderComponent.EMPTY_STRING_ARRAY;
    }
    const cached = this.filterValueCache.get(filter.key);
    if (cached && cached.length === current.length && cached.every((v, i) => v === current[i])) {
      return cached;
    }
    this.filterValueCache.set(filter.key, current);
    return current;
  }

  private computeGlobalFilterOptions(filter: GlobalFilter): string[] {
    const values = new Set<string>();
    const ancestorChain = this.getAncestorChain(filter);
    this.dashboardCards.forEach((card) => {
      if (!card.chart.dataset.columns.some((column) => column.name === filter.key)) {
        return;
      }
      let rows = card.chart.dataset.rows;
      for (const ancestor of ancestorChain) {
        const selected = this.activeGlobalFilters[ancestor.key];
        if (!selected || selected.length === 0) continue;
        if (!card.chart.dataset.columns.some((column) => column.name === ancestor.key)) continue;
        rows = rows.filter((row) => selected.includes(row[ancestor.key]));
      }
      rows.forEach((row) => {
        const value = row[filter.key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          values.add(String(value));
        }
      });
    });
    return Array.from(values).sort();
  }

  // Whether the cascade parent (if any) has at least one active value.
  // Top-level filters (no parent) are always considered enabled.
  isFilterEnabled(filter: GlobalFilter): boolean {
    if (!filter.parentKey) return true;
    const parentValues = this.activeGlobalFilters[filter.parentKey];
    return !!parentValues && parentValues.length > 0;
  }

  // Placeholder text for the dropdown:
  //  - top-level filter: "All"
  //  - cascading filter with parent not selected: "Select <parent> first"
  //  - cascading filter with parent selected: "All"
  getFilterPlaceholder(filter: GlobalFilter): string {
    if (!filter.parentKey) return 'All';
    if (this.isFilterEnabled(filter)) return 'All';
    const parent = this.dashboardGlobalFilters.find((f) => f.key === filter.parentKey);
    return `Select ${parent?.label || filter.parentKey} first`;
  }

  // Toggle every option in a filter (used by Select All buttons in the dropdown footer).
  toggleSelectAll(filter: GlobalFilter): void {
    const currentSelection = this.activeGlobalFilters[filter.key] || [];
    const options = this.getGlobalFilterOptions(filter);
    if (currentSelection.length === options.length) {
      this.onGlobalFilterChange(filter, []);
    } else {
      this.onGlobalFilterChange(filter, [...options]);
    }
  }

  isAllSelectedForFilter(filter: GlobalFilter): boolean {
    const selected = this.activeGlobalFilters[filter.key] || [];
    const options = this.getGlobalFilterOptions(filter);
    return options.length > 0 && selected.length === options.length;
  }

  getFilterDepth(filter: GlobalFilter): number {
    let depth = 0;
    let current: GlobalFilter | undefined = filter;
    const visited = new Set<string>();
    while (current?.parentKey && !visited.has(current.parentKey)) {
      visited.add(current.parentKey);
      const parent = this.dashboardGlobalFilters.find((f) => f.key === current!.parentKey);
      if (!parent) break;
      depth += 1;
      current = parent;
    }
    return depth;
  }

  getParentFilterLabel(filter: GlobalFilter): string {
    if (!filter.parentKey) return '';
    const parent = this.dashboardGlobalFilters.find((f) => f.key === filter.parentKey);
    return parent ? parent.label : filter.parentKey;
  }

  getCandidateParentFilters(): GlobalFilter[] {
    return this.dashboardGlobalFilters;
  }

  private getAncestorChain(filter: GlobalFilter): GlobalFilter[] {
    const chain: GlobalFilter[] = [];
    const visited = new Set<string>([filter.key]);
    let current: GlobalFilter | undefined = filter;
    while (current?.parentKey && !visited.has(current.parentKey)) {
      visited.add(current.parentKey);
      const parent = this.dashboardGlobalFilters.find((f) => f.key === current!.parentKey);
      if (!parent) break;
      chain.push(parent);
      current = parent;
    }
    return chain;
  }

  private invalidateChildFilterSelections(parentKey: string): void {
    const children = this.dashboardGlobalFilters.filter((f) => f.parentKey === parentKey);
    let mutated = false;
    for (const child of children) {
      const selected = this.activeGlobalFilters[child.key];
      if (!selected || !selected.length) continue;
      const validOptions = new Set(this.getGlobalFilterOptions(child));
      const filtered = selected.filter((v) => validOptions.has(v));
      if (filtered.length !== selected.length) {
        this.activeGlobalFilters = { ...this.activeGlobalFilters, [child.key]: filtered };
        mutated = true;
      }
      this.invalidateChildFilterSelections(child.key);
    }
    if (mutated) {
      this.scheduleDashboardRender();
    }
  }

  // ── Cross-stage chart actions (raised to the shell) ─────────────────────────

  /**
   * Edit a dashboard card's chart in place: ask the shell to load the underlying
   * saved chart back into the Chart Builder (mapping, formatting, filter keys,
   * etc.) and switch to the charts view so the user can change it. Saving from
   * there reuses the same chart id, so every dashboard card referencing it picks
   * up the edit.
   */
  editDashboardChart(card: DashboardCard, event?: Event): void {
    event?.stopPropagation();
    this.editChart.emit(card.chart);
    this.setViewMode('charts');
    this.saveMessage = `Editing "${card.chart.name}". Update the mapping, formatting, or filter keys, then Save to apply across the dashboard.`;
  }

  loadSavedChart(chart: SavedSelfServiceChart): void {
    // The chart's working state now lives in the Chart Builder. Forward the
    // request to the shell (which routes it to ChartBuilderComponent.loadSavedChart).
    this.editChart.emit(chart);
  }

  async deleteSavedChart(chart: SavedSelfServiceChart, event?: Event): Promise<void> {
    event?.stopPropagation();
    try {
      await firstValueFrom(this.chartService.deleteChart(chart.id));
      const nextCharts = this.savedCharts.filter((item) => item.id !== chart.id);

      if (this.persistence.persistSavedCharts(nextCharts)) {
        this.savedCharts = nextCharts;
        this.dashboardItems = this.dashboardItems.filter((item) => item.chartId !== chart.id);
        this.persistence.persistDashboardDraft(this.dashboardItems);
        if (this.state.activeSavedChartId() === chart.id) {
          this.state.setActiveSavedChartId('');
        }
        this.saveMessage = 'Saved chart deleted.';
        this.updateDashboardConfig();
        this.scheduleDashboardRender();
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to delete chart.';
    }
  }

  // ── Dashboard draft items (grid) ────────────────────────────────────────────
  removeDashboardItem(item: DashboardDraftItem): void {
    this.dashboardItems = this.dashboardItems.filter((current) => current.id !== item.id);
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.dashboardCharts.get(item.id)?.dispose();
    this.dashboardCharts.delete(item.id);
    this.activeSavedDashboardId = '';
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  addChartToDashboard(chart: SavedSelfServiceChart): void {
    this.dashboardItems = [
      ...this.dashboardItems,
      {
        id: this.createId(),
        chartId: chart.id,
        addedAt: new Date().toISOString(),
        colSpan: this.defaultSpanForChart(chart),
        // In tabbed mode the chart lands in the tab the user is editing.
        ...(this.useTabs && this.activeBuilderTabId ? { tabId: this.activeBuilderTabId } : {}),
      },
    ];

    if (this.persistence.persistDashboardDraft(this.dashboardItems)) {
      this.activeSavedDashboardId = '';
      this.showSavedDashboardPicker = false;
      this.markDraft();
      this.updateDashboardConfig();
      this.saveMessage = `Added "${chart.name}" to the dashboard.`;
      this.scheduleDashboardRender();
    }
  }

  getDashboardChartCount(chartId: string): number {
    return this.dashboardItems.filter((item) => item.chartId === chartId).length;
  }

  /** How many instances of this saved chart live in the tab currently being
   *  edited. In single-page (non-tabbed) mode this equals the total count. */
  getActiveTabChartCount(chartId: string): number {
    if (!this.useTabs) return this.getDashboardChartCount(chartId);
    const activeId = this.activeBuilderTabId;
    return this.dashboardItems.filter(
      (item) => item.chartId === chartId && item.tabId === activeId,
    ).length;
  }

  // ── Tabs (tabbed dashboards) ────────────────────────────────────────────────

  private createTabId(): string {
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  }

  /** Turn tabbed mode on: create a first tab and move all existing charts into
   *  it so nothing is orphaned. */
  enableTabs(): void {
    if (this.useTabs) return;
    const firstTab: DashboardTab = { id: this.createTabId(), label: 'Tab 1' };
    this.dashboardTabs = [firstTab];
    this.activeBuilderTabId = firstTab.id;
    // Assign every existing chart to the first tab.
    this.dashboardItems = this.dashboardItems.map((item) => ({ ...item, tabId: firstTab.id }));
    this.persistTabsAndItems();
  }

  /** Turn tabbed mode off: flatten back to a single-page dashboard. Charts keep
   *  their order; tab assignment is dropped. */
  disableTabs(): void {
    if (!this.useTabs) return;
    this.dashboardTabs = [];
    this.activeBuilderTabId = null;
    this.dashboardItems = this.dashboardItems.map(({ tabId, ...rest }) => rest);
    this.persistTabsAndItems();
  }

  toggleUseTabs(enabled: boolean): void {
    if (enabled) this.enableTabs();
    else this.disableTabs();
  }

  addTab(): void {
    const tab: DashboardTab = {
      id: this.createTabId(),
      label: `Tab ${this.dashboardTabs.length + 1}`,
    };
    this.dashboardTabs = [...this.dashboardTabs, tab];
    this.activeBuilderTabId = tab.id;
    this.persistTabsAndItems();
  }

  renameTab(tabId: string, label: string): void {
    const clean = (label || '').trim();
    if (!clean) return;
    this.dashboardTabs = this.dashboardTabs.map((tab) =>
      tab.id === tabId ? { ...tab, label: clean } : tab,
    );
    this.persistTabsAndItems();
  }

  /** Prompt for a new tab label (simple inline rename). */
  renameTabPrompt(tab: DashboardTab): void {
    const next = window.prompt('Rename tab', tab.label);
    if (next != null) this.renameTab(tab.id, next);
  }

  /** Delete a tab and every chart assigned to it. Keeps at least one tab. */
  deleteTab(tabId: string): void {
    if (this.dashboardTabs.length <= 1) return;
    const remaining = this.dashboardTabs.filter((tab) => tab.id !== tabId);
    this.dashboardTabs = remaining;
    // Drop charts that lived on the removed tab; dispose their chart instances.
    this.dashboardItems
      .filter((item) => item.tabId === tabId)
      .forEach((item) => {
        this.dashboardCharts.get(item.id)?.dispose();
        this.dashboardCharts.delete(item.id);
      });
    this.dashboardItems = this.dashboardItems.filter((item) => item.tabId !== tabId);
    if (this.activeBuilderTabId === tabId) {
      this.activeBuilderTabId = remaining[0]?.id ?? null;
    }
    this.persistTabsAndItems();
  }

  moveTab(tabId: string, direction: 'left' | 'right'): void {
    const index = this.dashboardTabs.findIndex((tab) => tab.id === tabId);
    if (index === -1) return;
    const target = direction === 'left' ? index - 1 : index + 1;
    if (target < 0 || target >= this.dashboardTabs.length) return;
    const next = [...this.dashboardTabs];
    [next[index], next[target]] = [next[target], next[index]];
    this.dashboardTabs = next;
    this.persistTabsAndItems();
  }

  switchBuilderTab(tabId: string): void {
    if (tabId === this.activeBuilderTabId) return;
    this.activeBuilderTabId = tabId;
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  /** Persist item + tab changes and refresh preview/config in one place. */
  private persistTabsAndItems(): void {
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.activeSavedDashboardId = '';
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  removeLatestDashboardChart(chartId: string): void {
    // In tabbed mode the card's Add/Remove operate on the active tab, so remove
    // the latest instance FROM THAT TAB — not the globally-latest, which could
    // sit in a tab the user isn't looking at.
    const activeId = this.activeBuilderTabId;
    const matches = (item: DashboardDraftItem): boolean =>
      item.chartId === chartId && (!this.useTabs || item.tabId === activeId);

    const index = [...this.dashboardItems].reverse().findIndex(matches);
    if (index === -1) {
      return;
    }

    const removeIndex = this.dashboardItems.length - 1 - index;
    const [removed] = this.dashboardItems.slice(removeIndex, removeIndex + 1);
    this.dashboardItems = this.dashboardItems.filter((_, itemIndex) => itemIndex !== removeIndex);
    this.persistence.persistDashboardDraft(this.dashboardItems);
    if (removed?.id) {
      this.dashboardCharts.get(removed.id)?.dispose();
      this.dashboardCharts.delete(removed.id);
    }
    this.activeSavedDashboardId = '';
    this.showSavedDashboardPicker = false;
    this.markDraft();
    this.updateDashboardConfig();
    this.saveMessage = 'Removed one chart from the dashboard draft.';
    this.scheduleDashboardRender();
  }

  moveDashboardItem(item: DashboardDraftItem, direction: 'up' | 'down'): void {
    const index = this.dashboardItems.findIndex((current) => current.id === item.id);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= this.dashboardItems.length) return;
    const next = [...this.dashboardItems];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    this.dashboardItems = next;
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.activeSavedDashboardId = '';
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  setCardSpan(item: DashboardDraftItem, span: number): void {
    const clamped = Math.min(this.clampSpan(span), this.dashboardColumns);
    this.dashboardItems = this.dashboardItems.map((current) =>
      current.id === item.id ? { ...current, colSpan: clamped } : current,
    );
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.activeSavedDashboardId = '';
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  getEffectiveCardSpan(card: DashboardCard): number {
    const requested = card.item.colSpan ?? this.defaultSpanForChart(card.chart);
    return Math.min(this.clampSpan(requested), this.dashboardColumns);
  }

  setGlobalFiltersPosition(position: GlobalFiltersPosition): void {
    if (this.dashboardGlobalFiltersPosition === position) {
      return;
    }
    this.dashboardGlobalFiltersPosition = position;
    this.markDraft();
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  setDashboardColumns(columns: number): void {
    this.dashboardColumns = this.clampColumns(columns);
    this.dashboardItems = this.dashboardItems.map((item) => ({
      ...item,
      colSpan: item.colSpan ? Math.min(item.colSpan, this.dashboardColumns) : item.colSpan,
    }));
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.activeSavedDashboardId = '';
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  // ── Save / publish ──────────────────────────────────────────────────────────
  async publishDashboard(): Promise<void> {
    this.errorMessage = '';
    this.saveMessage = '';

    if (!this.canSaveDashboard) {
      this.saveMessage = 'Add at least one chart and enter a dashboard name before publishing.';
      return;
    }

    this.dashboardActionPending = 'publish';
    try {
      this.dashboardStatus = 'published';
      this.dashboardPublishedAt = new Date().toISOString();
      await this.saveDashboard();
      if (!this.errorMessage) {
        this.syncSavedDashboardStatus(this.activeSavedDashboardId, 'published', this.dashboardPublishedAt);
        // Send full DVM JSON to the layout API.
        try {
          let emailId = '';
          if (await this.keycloak.isLoggedIn()) {
            const profile = await this.keycloak.loadUserProfile();
            emailId = profile.email || '';
          }
          await firstValueFrom(
            this.dashboardService.publishLayoutToApi(this.persistence.buildDashboardConfig(), emailId, this.dashboardName),
          );
          this.saveMessage = 'Dashboard published.';
          window.dispatchEvent(new CustomEvent('dvm:dashboard-published'));
        } catch {
          this.errorMessage = 'Unable to publish dashboard.';
        }
        if (!this.errorMessage) {
          const slug = this.dashboardNameToSlug(this.dashboardName);
          if (slug) {
            // Navigate to the API-backed /dynamic-dashboard/<slug> route (the
            // SAME one the sidebar uses), NOT the localStorage /<slug> route.
            // The localStorage `savedDashboards.config` is stored dataset-STRIPPED
            // (persistSavedDashboards drops selfService.rawRows to stay under the
            // quota), so the /<slug> route rendered blank charts right after
            // publish. The API holds the FULL config (publish just sent it), so
            // /dynamic-dashboard/<slug> renders the charts correctly — identical
            // to clicking the dashboard in the sidebar.
            //
            // Keep the loader up through the navigation itself — router.navigate
            // resolves only once the destination route has activated, so
            // clearing dashboardActionPending in `finally` (below) doesn't
            // happen mid-transition and flash the button back to idle while
            // the published screen is still loading in.
            await this.router.navigate(['/dynamic-dashboard', slug]);
          }
        }
      }
    } finally {
      this.dashboardActionPending = null;
    }
  }

  /** Update the in-memory savedDashboards entry and re-persist to localStorage
   *  so publish/unpublish status survives a reload, independent of whatever the
   *  save API echoed back. */
  private syncSavedDashboardStatus(
    dashboardId: string,
    status: DashboardStatus,
    publishedAt: string | null,
  ): void {
    let changed = false;
    const next = this.savedDashboards.map((dashboard) => {
      if (dashboard.id !== dashboardId) return dashboard;
      changed = true;
      return { ...dashboard, status, publishedAt: publishedAt || undefined };
    });
    if (changed && this.persistence.persistSavedDashboards(next)) {
      this.savedDashboards = next;
    }
  }

  private dashboardNameToSlug(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async unpublishDashboard(): Promise<void> {
    this.dashboardActionPending = 'unpublish';
    try {
      this.dashboardStatus = 'draft';
      this.dashboardPublishedAt = null;
      await this.saveDashboard();
      if (!this.errorMessage && this.activeSavedDashboardId) {
        try {
          await firstValueFrom(this.dashboardService.unpublishDashboard(this.activeSavedDashboardId));
          this.saveMessage = 'Dashboard reverted to draft.';
        } catch (error) {
          this.errorMessage = error instanceof Error ? error.message : 'Unable to unpublish dashboard.';
        }
        // Keep the stored record in sync so it doesn't reload as 'published'.
        if (!this.errorMessage) {
          this.syncSavedDashboardStatus(this.activeSavedDashboardId, 'draft', null);
        }
      }
    } finally {
      this.dashboardActionPending = null;
    }
  }

  async saveDashboard(): Promise<void> {
    // publishDashboard/unpublishDashboard call this as a sub-step and already
    // own dashboardActionPending — only claim it here when called directly
    // (the "Save"/"Update Dashboard" button), so we don't clear their spinner
    // early once this sub-step resolves.
    const ownsPendingState = this.dashboardActionPending === null;
    if (ownsPendingState) this.dashboardActionPending = 'save';
    try {
      await this.saveDashboardInternal();
    } finally {
      if (ownsPendingState) this.dashboardActionPending = null;
    }
  }

  private async saveDashboardInternal(): Promise<void> {
    this.errorMessage = '';
    this.saveMessage = '';
    this.updateDashboardConfig();

    if (!this.canSaveDashboard) {
      this.saveMessage = 'Add at least one saved chart and enter a dashboard name before saving.';
      return;
    }

    const now = new Date().toISOString();
    const dashboardName = this.dashboardName.trim();
    const matchingDashboards = this.findMatchingDashboards(dashboardName);
    const activeExisting = matchingDashboards.find((dashboard) => dashboard.id === this.activeSavedDashboardId);
    const existing = activeExisting || matchingDashboards[0];
    // Backfill item.id for any items that pre-date per-item-ids — needed for
    // unique widgetId derivation in buildWidgetEntry.
    const itemsToSave = this.dashboardItems.map((item) => ({
      ...item,
      id: item.id || this.createId(),
    }));
    this.dashboardItems = itemsToSave.map((item) => ({ ...item }));

    if (existing && !this.activeSavedDashboardId) {
      this.activeSavedDashboardId = existing.id;
    }

    const savedDashboard: SavedSelfServiceDashboard = {
      id: existing?.id || this.createId(),
      name: dashboardName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      items: itemsToSave,
      globalFilters: this.dashboardGlobalFilters.map((filter) => ({ ...filter })),
      globalFiltersPosition: this.dashboardGlobalFiltersPosition,
      layout: { columns: this.dashboardColumns },
      status: this.dashboardStatus,
      publishedAt: this.dashboardPublishedAt || undefined,
      ...(this.useTabs ? { tabs: this.dashboardTabs.map((tab) => ({ ...tab })) } : {}),
      config: this.persistence.buildDashboardConfigFromItems(
        dashboardName,
        itemsToSave,
        this.dashboardColumns,
        this.dashboardStatus,
        this.dashboardPublishedAt,
        this.dashboardGlobalFilters,
        this.dashboardGlobalFiltersPosition,
        this.dashboardTabs,
      ),
    };

    try {
      const persistedDashboard = await firstValueFrom(this.dashboardService.saveDashboard(savedDashboard));
      const persistedNextDashboards = existing
        ? [
            persistedDashboard,
            ...this.savedDashboards.filter((dashboard) => (
              dashboard.id !== existing.id
              && this.normalizeDashboardName(dashboard.name) !== this.normalizeDashboardName(dashboardName)
            )),
          ]
        : [persistedDashboard, ...this.savedDashboards];

      if (this.persistence.persistSavedDashboards(persistedNextDashboards)) {
        this.savedDashboards = persistedNextDashboards;
        this.activeSavedDashboardId = persistedDashboard.id;
        this.showSavedDashboardPicker = false;
        this.generatedDashboardConfig = JSON.stringify(persistedDashboard.config, null, 2);
        this.saveMessage = existing ? 'Dashboard updated.' : 'Dashboard saved.';
        this.showDashboardPreviewStep();
        this.scheduleDashboardRender();
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to save dashboard.';
    }
  }

  loadSavedDashboard(dashboard: SavedSelfServiceDashboard): void {
    // API-only dashboard (published server-side, no editable local copy on this
    // machine): the API layout_json doesn't carry editable builder items, so we
    // can't rehydrate the builder. Open the published view instead — the
    // `/dynamic-dashboard/<slug>` route resolves for any user (falls back to
    // fetch-by-slug when localStorage is cold).
    if (this.apiOnlyDashboardSlugs.has(this.dashboardNameToSlug(dashboard.name))) {
      const slug = this.dashboardNameToSlug(dashboard.name);
      if (slug) {
        this.router.navigate(['/dynamic-dashboard', slug]);
      }
      return;
    }

    this.errorMessage = '';
    this.validationMessage = '';
    this.saveMessage = '';
    this.activeSavedDashboardId = dashboard.id;
    this.dashboardName = dashboard.name;
    this.dashboardItems = dashboard.items.map((item) => ({ ...item }));
    this.dashboardGlobalFilters = Array.isArray(dashboard.globalFilters)
      ? dashboard.globalFilters.map((filter) => ({ ...filter }))
      : [];
    this.dashboardGlobalFiltersPosition = this.normalizeFiltersPosition(dashboard.globalFiltersPosition);
    this.dashboardColumns = this.clampColumns(dashboard.layout?.columns ?? this.dashboardColumns);
    this.dashboardStatus = dashboard.status === 'published' ? 'published' : 'draft';
    this.dashboardPublishedAt = dashboard.publishedAt || null;
    // Restore tab state (if any) and default the active tab to the first one.
    this.dashboardTabs = Array.isArray(dashboard.tabs)
      ? dashboard.tabs.map((tab) => ({ ...tab }))
      : [];
    this.activeBuilderTabId = this.dashboardTabs[0]?.id ?? null;
    this.activeGlobalFilters = {};
    this.showSavedDashboardPicker = false;
    this.setViewMode('dashboards');
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
    this.showDashboardPreviewStep();
    this.scheduleDashboardRender();
  }

  private restoreDashboardBuilderReturnTarget(): void {
    // A one-shot "open on Setup" intent (e.g. chart-builder's "Add to dashboard")
    // takes precedence over any stale router/history return target that would
    // otherwise jump us to Arrange/Preview. ngAfterViewInit applies the step.
    if (this.state.pendingDashboardStep() === 'setup') {
      this.setViewMode('dashboards');
      return;
    }

    const navState = (this.router.getCurrentNavigation()?.extras.state || history.state || {}) as {
      selfServiceView?: string;
      dashboardSlug?: string;
      dashboardId?: string;
      preview?: boolean;
    };
    const query = this.route.snapshot.queryParamMap;
    const requestedView = navState.selfServiceView || query.get('view') || '';
    const shouldOpenDashboard = requestedView === 'dashboards' || query.get('preview') === 'true' || navState.preview;

    if (!shouldOpenDashboard) {
      return;
    }

    const dashboardSlug = (navState.dashboardSlug || query.get('dashboard') || '').trim();
    const dashboardId = (navState.dashboardId || query.get('dashboardId') || '').trim();
    // Only a LOCAL editable copy counts here (items present). An API-only entry
    // merged in from the sidebar list (issue 2) has empty items, so we let it
    // fall through to the API restore below which rebuilds it editable.
    const matchingDashboard = this.savedDashboards.find((dashboard) => {
      if (this.isApiOnlyDashboard(dashboard)) return false;
      if (dashboardId && dashboard.id === dashboardId) {
        return true;
      }
      return !!dashboardSlug && this.dashboardNameToSlug(dashboard.name) === dashboardSlug;
    });

    if (matchingDashboard) {
      this.loadSavedDashboard(matchingDashboard);
      return;
    }

    // No editable local copy (opened from the sidebar / after logout / another
    // machine). Fetch the published dashboard's rendered layout and rebuild an
    // EDITABLE builder from it — works for OLD and NEW published dashboards
    // alike. The user can then edit + re-save through the API. Falls back to the
    // read-only preview only if nothing resolves.
    if (dashboardSlug) {
      this.restoreDashboardFromApiBySlug(dashboardSlug, dashboardId);
      return;
    }

    this.setViewMode('dashboards');
    this.showDashboardPreviewStep();
  }

  /** Reconstruct a fully editable builder from a published dashboard's rendered
   *  layout — for ANY published dashboard, old or new. Primary source is the
   *  `dvm.dynamicDashboard.<slug>` cache the published view just wrote (exact,
   *  slug-keyed, and the reliable source — `getPublishedLayoutBySlug`'s
   *  name→slug select-all match frequently misses for dynamic dashboards).
   *  Falls back to `getPublishedLayoutBySlug` only when the cache is cold. */
  private restoreDashboardFromApiBySlug(slug: string, dashboardId: string): void {
    // Fast path: the published view we came from cached this exact slug's
    // rendered layout. Use it immediately if it has renderable content.
    const cached = readDynamicDashboardCache<Record<string, unknown> | null>(slug, null);
    if (cached && this.hasRenderableContent(cached)) {
      this.applyRebuiltDashboard(cached, slug, dashboardId);
      return;
    }

    // Otherwise fetch from the API. getPublishedLayoutBySlug needs the current
    // user's email — on this backend the email-independent select returns an
    // empty list (COMMIT_ROLLBACK), so it resolves via the email-filtered
    // dynamic_filter endpoint (same as the published view + sidenav). Resolve
    // the email async (the Keycloak token may not be parsed yet in a fresh
    // session) before fetching.
    this.restoreFromApiSub?.unsubscribe();
    (async () => {
      let emailId = '';
      try {
        if (await this.keycloak.isLoggedIn()) {
          const profile = await this.keycloak.loadUserProfile();
          emailId = profile.email || '';
        }
      } catch { /* email unavailable — fall through to cache below */ }

      this.restoreFromApiSub = this.dashboardService
        .getPublishedLayoutBySlug(slug, emailId)
        .subscribe((config) => {
          // Prefer the API config; if it's empty/unresolved, fall back to any
          // cached copy (even one without much content) so we still try.
          const resolved = config && this.hasRenderableContent(config) ? config : (cached ?? config);
          this.applyRebuiltDashboard(resolved, slug, dashboardId);
        });
    })();
  }

  /** True when a published layout_json actually carries widgets to reconstruct
   *  (single-page pageContent rows, or any tab's pageContent). Guards against
   *  wasting the fast-path on a name-only / empty cache entry. */
  private hasRenderableContent(config: Record<string, unknown> | null): boolean {
    const cfg = config as any;
    if (!cfg || typeof cfg !== 'object') return false;
    if (Array.isArray(cfg.pageContent) && cfg.pageContent.length) return true;
    if (Array.isArray(cfg.tabs)) {
      return cfg.tabs.some((t: any) => Array.isArray(t?.pageContent) && t.pageContent.length);
    }
    return false;
  }

  /** Rebuild + open an editable dashboard from a resolved published layout. */
  private applyRebuiltDashboard(
    config: Record<string, unknown> | null,
    slug: string,
    dashboardId: string,
  ): void {
    const rebuilt = this.persistence.reconstructDashboardFromRendered(
      config,
      dashboardId || `api:${slug}`,
    );
    if (!rebuilt) {
      this.setViewMode('dashboards');
      this.showDashboardPreviewStep();
      return;
    }

    // Register the reconstructed charts so resolveDashboardChart (which reads
    // state.savedCharts by item.chartId) finds them — otherwise the cards
    // render blank after logout when the in-memory chart set is empty.
    const existingChartIds = new Set(this.savedCharts.map((chart) => chart.id));
    const chartsToAdd = rebuilt.charts.filter((chart) => !existingChartIds.has(chart.id));
    if (chartsToAdd.length) {
      this.savedCharts = [...this.savedCharts, ...chartsToAdd];
      this.persistence.persistSavedCharts(this.savedCharts);
    }

    // Now editable locally: drop it from the API-only (read-only) set and
    // persist it into savedDashboards so a later open is a plain local hit.
    const rebuiltSlug = this.dashboardNameToSlug(rebuilt.dashboard.name);
    this.apiOnlyDashboardSlugs.delete(rebuiltSlug);
    const withoutStale = this.savedDashboards.filter(
      (dashboard) => this.dashboardNameToSlug(dashboard.name) !== rebuiltSlug,
    );
    const nextDashboards = [rebuilt.dashboard, ...withoutStale];
    if (this.persistence.persistSavedDashboards(nextDashboards)) {
      this.savedDashboards = nextDashboards;
    }

    this.loadSavedDashboard(rebuilt.dashboard);
  }

  onDashboardPickerChange(dashboardId: string | null): void {
    if (!dashboardId) {
      this.startNewDashboard();
      return;
    }

    const dashboard = this.savedDashboards.find((item) => item.id === dashboardId);
    if (dashboard) {
      this.loadSavedDashboard(dashboard);
    }
  }

  /** True for a dashboard listed from the API with no editable local copy.
   *  Such entries open the published view and can't be edited or deleted here. */
  isApiOnlyDashboard(dashboard: SavedSelfServiceDashboard): boolean {
    return this.apiOnlyDashboardSlugs.has(this.dashboardNameToSlug(dashboard.name));
  }

  async deleteSavedDashboard(dashboard: SavedSelfServiceDashboard, event?: Event): Promise<void> {
    event?.stopPropagation();
    // API-only entries live server-side, not in localStorage. There's no
    // editable local record to delete, and removing the server copy is out of
    // scope for the builder (it would just re-appear on the next API fetch), so
    // deletion is disabled for them.
    if (this.isApiOnlyDashboard(dashboard)) {
      return;
    }
    try {
      await firstValueFrom(this.dashboardService.deleteDashboard(dashboard.id));
      const nextDashboards = this.savedDashboards.filter((item) => item.id !== dashboard.id);

      if (this.persistence.persistSavedDashboards(nextDashboards)) {
        this.savedDashboards = nextDashboards;
        if (this.activeSavedDashboardId === dashboard.id) {
          this.activeSavedDashboardId = '';
        }
        this.saveMessage = 'Saved dashboard deleted.';
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to delete dashboard.';
    }
  }

  isPublished(dashboard: SavedSelfServiceDashboard): boolean {
    return dashboard.status === 'published';
  }

  private markDraft(): void {
    if (this.dashboardStatus === 'published') {
      this.dashboardStatus = 'draft';
      this.dashboardPublishedAt = null;
    }
  }

  clearDashboardDraft(): void {
    this.dashboardItems = [];
    this.dashboardGlobalFilters = [];
    this.dashboardTabs = [];
    this.activeBuilderTabId = null;
    this.activeGlobalFilters = {};
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.dashboardCharts.forEach((chart) => chart.dispose());
    this.dashboardCharts.clear();
    this.activeSavedDashboardId = '';
    this.showSavedDashboardPicker = true;
    this.updateDashboardConfig();
    this.saveMessage = 'Dashboard draft cleared.';
  }

  startNewDashboard(): void {
    this.dashboardName = '';
    this.activeSavedDashboardId = '';
    this.dashboardItems = [];
    this.dashboardGlobalFilters = [];
    this.dashboardTabs = [];
    this.activeBuilderTabId = null;
    this.activeGlobalFilters = {};
    this.dashboardColumns = 3;
    this.dashboardGlobalFiltersPosition = 'top';
    this.dashboardStatus = 'draft';
    this.dashboardPublishedAt = null;
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.dashboardCharts.forEach((chart) => chart.dispose());
    this.dashboardCharts.clear();
    this.showSavedDashboardPicker = true;
    this.updateDashboardConfig();
    this.saveMessage = '';
    if (this.dashboardStepper) {
      this.dashboardStepper.selectedIndex = 0;
    }
    setTimeout(() => {
      const input = this.dashboardNameInput?.nativeElement;
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus();
    }, 80);
  }

  onDashboardNameChange(): void {
    this.activeSavedDashboardId = '';
    this.showSavedDashboardPicker = false;
    this.markDraft();
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.updateDashboardConfig();
  }

  // ── Saved-dashboard / draft loading ─────────────────────────────────────────
  private loadSavedDashboards(): void {
    // readJson transparently handles compressed (new) and legacy plaintext (old)
    // payloads; returns [] on any decode failure so we can't throw here.
    const dashboards = readJson<SavedSelfServiceDashboard[]>(
      DashboardBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY,
      [],
    );
    this.savedDashboards = this.persistence.consolidateSavedDashboards(dashboards);
    // One-time storage migration: existing users have savedDashboards whose
    // `config` still embeds a full copy of every chart's dataset rows (written
    // before persistSavedDashboards learned to strip them). Re-persist once on
    // load so that duplicated row payload is dropped from localStorage, freeing
    // the quota that was causing "Unable to save dashboard locally.". This is
    // safe: config is rebuilt from savedCharts + items whenever rows are needed.
    // Always re-persist (not only when consolidation changed the count) so the
    // strip actually runs for users whose list is otherwise unchanged.
    this.persistence.persistSavedDashboards(this.savedDashboards);
    // The Saved dashboards tab is source-of-truth from the SAME API the sidenav
    // uses (the email-filtered `dynamic_filter` list of the current user's own
    // published dashboards). Merge those in on top of the local list so the tab
    // reflects what's actually published server-side, not just this browser.
    this.reconcileSavedDashboardsFromApi();
  }

  /** Fetch the current user's published dashboards from the API and union them
   *  into `savedDashboards`. A local editable copy (matched by name-slug) always
   *  wins so click-to-edit keeps working; API-only dashboards are added as
   *  display/open entries and tracked in `apiOnlyDashboardSlugs`. */
  private reconcileSavedDashboardsFromApi(): void {
    this.ownedDashboardsSub?.unsubscribe();
    this.ownedDashboardsSub = (async () => {
      let emailId = '';
      try {
        if (await this.keycloak.isLoggedIn()) {
          const profile = await this.keycloak.loadUserProfile();
          emailId = profile.email || '';
        }
      } catch { /* not logged in / profile unavailable — API list stays empty */ }
      if (!emailId) return;

      this.ownedDashboardsSub = this.dashboardService
        .fetchOwnedDashboardsFromApi(emailId)
        .subscribe((owned) => this.mergeApiDashboards(owned));
    })() as unknown as Subscription;
  }

  /** Merge API-published dashboards into the local `savedDashboards` list.
   *  Local entries (by name-slug) take precedence — the API copy is only added
   *  when there's no local editable copy for that slug. */
  private mergeApiDashboards(owned: OwnedDashboardLayout[]): void {
    if (!owned.length) return;

    const localSlugs = new Set(
      this.savedDashboards.map((d) => this.dashboardNameToSlug(d.name)),
    );
    const additions: SavedSelfServiceDashboard[] = [];

    for (const item of owned) {
      // A local editable copy exists → keep it (click-to-edit works). Skip the
      // API copy so we don't double-list or shadow the editable one.
      if (localSlugs.has(item.slug)) continue;

      this.apiOnlyDashboardSlugs.add(item.slug);
      additions.push(this.toApiOnlySavedDashboard(item));
      localSlugs.add(item.slug);
    }

    if (additions.length) {
      // API-published dashboards first (they're the shared source of truth),
      // then the purely-local drafts/saves.
      this.savedDashboards = [...additions, ...this.savedDashboards];
    }
  }

  /** Build a display-only `SavedSelfServiceDashboard` from an API layout_json.
   *  It has NO editable `items` (the API doesn't carry them on this branch), so
   *  its slug is tracked in `apiOnlyDashboardSlugs` and clicking it opens the
   *  published view rather than the builder. */
  private toApiOnlySavedDashboard(item: OwnedDashboardLayout): SavedSelfServiceDashboard {
    const now = new Date().toISOString();
    return {
      id: `api:${item.slug}`,
      name: item.name,
      createdAt: now,
      updatedAt: now,
      items: [],
      status: 'published',
      publishedAt: now,
      config: item.layoutJson,
    };
  }

  private loadDashboardDraft(): void {
    try {
      const draft = readJson<unknown>(DashboardBuilderComponent.DASHBOARD_DRAFT_STORAGE_KEY, null);
      if (Array.isArray(draft)) {
        this.dashboardItems = draft;
        this.linkDraftToSavedDashboardByName();
        this.showSavedDashboardPicker = !this.hasActiveDashboardDraft;
        return;
      }

      const state = draft as Partial<DashboardDraftState> | null;
      this.dashboardName = state?.name || this.dashboardName;
      this.dashboardItems = Array.isArray(state?.items) ? state.items : [];
      this.dashboardGlobalFilters = Array.isArray(state?.globalFilters) ? state.globalFilters : [];
      this.dashboardGlobalFiltersPosition = this.normalizeFiltersPosition(state?.globalFiltersPosition);
      this.dashboardColumns = this.clampColumns(state?.layout?.columns ?? this.dashboardColumns);
      this.dashboardTabs = Array.isArray(state?.tabs) ? state.tabs : [];
      this.activeBuilderTabId = this.dashboardTabs[0]?.id ?? null;
      this.activeGlobalFilters = {};
      this.linkDraftToSavedDashboardByName();
      this.showSavedDashboardPicker = !this.hasActiveDashboardDraft;
    } catch {
      this.dashboardItems = [];
      this.dashboardGlobalFilters = [];
      this.dashboardTabs = [];
      this.activeBuilderTabId = null;
      this.activeGlobalFilters = {};
      this.showSavedDashboardPicker = true;
      this.errorMessage = 'Dashboard draft could not be loaded from this browser.';
    }
  }

  private linkDraftToSavedDashboardByName(): void {
    if (this.activeSavedDashboardId) return;
    const normalizedName = this.normalizeDashboardName(this.dashboardName);
    if (!normalizedName) return;
    const match = this.savedDashboards.find(
      (dashboard) => this.normalizeDashboardName(dashboard.name) === normalizedName,
    );
    if (match) {
      this.activeSavedDashboardId = match.id;
    }
  }

  // ── Small private helpers (copied from the monolith) ────────────────────────
  private clampColumns(n: number | undefined): number {
    const value = Number(n);
    if (!Number.isFinite(value)) return 3;
    return Math.max(1, Math.min(4, Math.round(value)));
  }

  private normalizeFiltersPosition(value: GlobalFiltersPosition | string | undefined): GlobalFiltersPosition {
    return value === 'left' || value === 'right' ? value : 'top';
  }

  private clampSpan(n: number | undefined): number {
    const value = Number(n);
    if (!Number.isFinite(value)) return 1;
    return Math.max(1, Math.min(4, Math.round(value)));
  }

  private defaultSpanForChart(chart: SavedSelfServiceChart): number {
    const kind = this.chartTypes.find((t) => t.value === chart.chartType)?.previewKind;
    if (kind === 'kpi') return 1;
    if (kind === 'table') return 2;
    // Compact tree — one row span keeps the card tight to the content.
    if (kind === 'generic-drill-down') return 1;
    return 1;
  }

  private findMatchingDashboards(name: string): SavedSelfServiceDashboard[] {
    const normalizedName = this.normalizeDashboardName(name);
    if (!normalizedName) {
      return [];
    }

    return this.savedDashboards.filter((dashboard) => (
      dashboard.id === this.activeSavedDashboardId
      || this.normalizeDashboardName(dashboard.name) === normalizedName
    ));
  }

  private normalizeDashboardName(name: string): string {
    return name.trim().toLowerCase();
  }

  private createId(): string {
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private createDefaultStyle(): ChartStyle {
    return {
      titleOverride: '',
      showLegend: true,
      legendPosition: 'bottom',
      showLabels: false,
      labelMode: 'value',
      palette: 'default',
      xAxisLabel: '',
      yAxisLabel: '',
      xAxisLabel2: '',
      yAxisLabel2: '',
      quadrantTopLeftLabel: '',
      quadrantTopRightLabel: '',
      quadrantBottomLeftLabel: '',
      quadrantBottomRightLabel: '',
      numberFormat: 'plain',
      numberScale: 'none',
      decimals: 0,
      comparisonValue: null,
      tableRowLimit: 50,
      tooltipEnabled: true,
      tooltipTrigger: 'auto',
      tooltipBackground: '#1e293b',
      tooltipTextColor: '#f8fafc',
      tooltipBorderColor: '#334155',
      tooltipBorderRadius: 6,
      tooltipFontSize: 12,
      tooltipAxisPointer: 'line',
    };
  }

  private normalizeStyle(style?: Partial<ChartStyle> | null): ChartStyle {
    return { ...this.createDefaultStyle(), ...(style || {}) };
  }

  /** Snapshot of the component state the chart-option builder needs. Read the
   *  current shape options (set just before each render) plus the available
   *  chart types and aggregation definitions. */
  private buildChartOptionContext(): ChartOptionContext {
    return {
      chartTypes: this.chartTypes,
      shapeOptions: this.currentShapeOptions,
      customAggregations: this.state.customAggregations(),
      builtinAggregations: this.builtinAggregations,
    };
  }

  // ── Card classification + KPI/table display ─────────────────────────────────
  isEChartsCard(card: DashboardCard): boolean {
    const kind = this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind;
    return kind !== 'kpi' && kind !== 'table' && kind !== 'generic-drill-down' && !isWidgetPreviewKind(kind);
  }

  isKpiCard(card: DashboardCard): boolean {
    return this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind === 'kpi';
  }

  isTableCard(card: DashboardCard): boolean {
    return this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind === 'table';
  }

  isDrillDownCard(card: DashboardCard): boolean {
    return this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind === 'generic-drill-down';
  }

  /** Memoized `data` inputs for the live drill-down component per card, so the
   *  template getter returns a stable reference (rebuilding every change-
   *  detection tick would make GenericDrillDownComponent re-fetch endlessly). */
  private drillDownCardData = new Map<string, Record<string, unknown>>();

  /**
   * Build the self-service `data` object the real GenericDrillDownComponent
   * consumes in the arrange view — identical to the published path. The
   * component calls ConfigService.getConfig, which sees `selfService` and runs
   * computeSelfServiceData → reshapeDrillDown, so the builder shows the exact
   * same interactive tree as the published dashboard.
   */
  getDrillDownCardData(card: DashboardCard): Record<string, unknown> {
    // Use the CROSS-FILTERED dataset (not the raw chart dataset) so the
    // drill-down REACTS as a listener when another card publishes a filter —
    // mirroring the table and ECharts cards. Without this the tree always shows
    // every row regardless of active cross-filters.
    const dataset = this.getFilteredDatasetForCard(card);
    const rows = dataset?.rows || [];
    const columns = (dataset?.columns || []).map((c) => ({ name: c.name, type: c.type }));
    const columnNames = columns.map((c) => c.name);

    // Cache-key on the identity that affects the rendered tree. When the dataset,
    // column set, OR the active cross-filters change, the key changes and we
    // rebuild (the component's @Input change then re-ingests). The filter
    // signature must be included or a filtered-then-cleared tree would keep
    // showing the stale, narrowed rows.
    const filterSig = this.crossFilterSignatureFor(card);
    const key = `${card.item.id}|${card.chart.id}|${rows.length}|${columnNames.join(',')}|${filterSig}`;
    const cached = this.drillDownCardData.get(card.item.id);
    if (cached && cached['__key'] === key) return cached;

    const widgetId = `ss-dd-${card.item.id}`;
    const data: Record<string, unknown> = {
      __key: key,
      widgetId,
      type: 'generic-drill-down',
      apiUrl: 'self-service://local',
      title: card.chart.name,
      // Fill the card; the arrange-view CSS gives .dashboard-drilldown-live a
      // definite height so the tree scrolls INSIDE the card (no growth/gap).
      height: '100%',
      columnLabels: Object.fromEntries(columnNames.map((n) => [n, n])),
      selfService: {
        rawRows: rows,
        columns,
        chartType: 'generic-drill-down',
        categoryKey: '',
        valueKey: '',
        tableColumns: columnNames,
        selfWidgetId: widgetId,
      },
    };
    this.drillDownCardData.set(card.item.id, data);
    return data;
  }

  isWidgetCard(card: DashboardCard): boolean {
    return isWidgetPreviewKind(this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind);
  }

  widgetKindForCard(card: DashboardCard): ChartPreviewKind {
    return this.chartTypes.find((t) => t.value === card.chart.chartType)?.previewKind || 'input-box';
  }

  /** Persist an interactive widget edit (notes text, uploaded file names, …)
   *  made on an arrange-step card back into its saved chart and the draft. */
  onWidgetConfigChange(card: DashboardCard, config: WidgetConfig): void {
    const nextCharts = this.savedCharts.map((chart) =>
      chart.id === card.chart.id ? { ...chart, widgetConfig: config } : chart,
    );
    if (this.persistence.persistSavedCharts(nextCharts)) {
      this.savedCharts = nextCharts;
      this.persistence.persistDashboardDraft(this.dashboardItems);
      this.activeSavedDashboardId = '';
      this.markDraft();
      this.updateDashboardConfig();
    }
  }

  getChartIconKind(chart: SavedSelfServiceChart): ChartPreviewKind {
    return this.chartTypes.find((t) => t.value === chart.chartType)?.previewKind || 'axis-bar';
  }

  getChartTypeLabel(chart: SavedSelfServiceChart): string {
    return this.chartTypes.find((t) => t.value === chart.chartType)?.label || chart.chartType;
  }

  trackDashboardCard(_index: number, card: DashboardCard): string {
    return card.item.id;
  }

  getKpiDisplay(card: DashboardCard): {
    label: string;
    value: string;
    aggregation: string;
    comparisonText: string;
    comparisonDirection: 'up' | 'down' | 'flat';
  } {
    const style = this.normalizeStyle(card.chart.style);
    const dataset = this.getFilteredDatasetForCard(card);
    const valueColumn = card.chart.mapping.value || '';
    const aggregation = card.chart.aggregation || 'sum';
    const columnType = dataset.columns.find((column) => column.name === valueColumn)?.type;
    const rawValues = dataset.rows.map((row) => String(row[valueColumn] ?? '').trim());
    const nonEmptyValues = rawValues.filter((value) => value !== '');
    const numbers = rawValues.map((value) => this.dataUtils.toNumber(value));
    const raw = this.dataUtils.aggregateValues(numbers, aggregation);
    const numericResult = columnType === 'number' || ['count', 'distinctCount'].includes(aggregation);
    const textResult = aggregation === 'last'
      ? (nonEmptyValues[nonEmptyValues.length - 1] || '')
      : (nonEmptyValues[0] || '');
    const value = numericResult ? this.dataUtils.formatNumber(raw, style) : (textResult || '-');

    let comparisonText = '';
    let comparisonDirection: 'up' | 'down' | 'flat' = 'flat';
    if (numericResult && style.comparisonValue != null && Number.isFinite(style.comparisonValue)) {
      const target = Number(style.comparisonValue);
      const delta = raw - target;
      const pct = target !== 0 ? (delta / target) * 100 : 0;
      comparisonDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
      const sign = delta > 0 ? '+' : '';
      comparisonText = `${sign}${pct.toFixed(1)}% vs ${this.dataUtils.formatNumber(target, style)}`;
    }

    return {
      label: style.titleOverride || valueColumn || 'KPI',
      value,
      aggregation: String(aggregation),
      comparisonText,
      comparisonDirection,
    };
  }
  // ── Arrange-view data-table column management (per card) ────────────────────
  // Mirrors the runtime TableComponent controls (hide/remove, reorder, sort,
  // reset) but on this view's static table preview. State is keyed by card id.
  private tableHiddenColumns = new Map<string, Set<string>>();
  private tableColumnOrder = new Map<string, string[]>();
  private tableSort = new Map<string, { column: string; dir: 'asc' | 'desc' }>();
  /** Which card's Columns dropdown is currently open (null = none). */
  openTableColumnMenu: string | null = null;

  private hiddenSetFor(card: DashboardCard): Set<string> {
    let set = this.tableHiddenColumns.get(card.item.id);
    if (!set) {
      set = new Set<string>();
      this.tableHiddenColumns.set(card.item.id, set);
    }
    return set;
  }

  private seedTableState(card: DashboardCard, datasetColumns: DatasetColumn[]): void {
    const columnNames = datasetColumns.map((column) => column.name);
    if (!this.tableColumnOrder.has(card.item.id)) {
      const savedOrder = card.item.tableColumnOrder?.length
        ? card.item.tableColumnOrder
        : card.item.tableColumns;
      if (savedOrder?.length) {
        const ordered = savedOrder.filter((name) => columnNames.includes(name));
        for (const name of columnNames) {
          if (!ordered.includes(name)) ordered.push(name);
        }
        this.tableColumnOrder.set(card.item.id, ordered);
      }
    }

    if (!this.tableHiddenColumns.has(card.item.id) && card.item.tableColumns?.length) {
      const visible = new Set(card.item.tableColumns.filter((name) => columnNames.includes(name)));
      if (visible.size) {
        this.tableHiddenColumns.set(
          card.item.id,
          new Set(columnNames.filter((name) => !visible.has(name))),
        );
      }
    }
  }

  /** Full ordered column list for a card, seeded from the dataset on first use. */
  private orderedColumnsFor(card: DashboardCard, datasetColumns: DatasetColumn[]): DatasetColumn[] {
    this.seedTableState(card, datasetColumns);
    const byName = new Map(datasetColumns.map((c) => [c.name, c]));
    const savedOrder = this.tableColumnOrder.get(card.item.id);
    if (!savedOrder) {
      return datasetColumns;
    }
    // Keep saved order for known columns, append any new columns from the dataset.
    const ordered = savedOrder.filter((n) => byName.has(n)).map((n) => byName.get(n)!);
    for (const col of datasetColumns) {
      if (!savedOrder.includes(col.name)) ordered.push(col);
    }
    return ordered;
  }

  private persistTableState(card: DashboardCard): void {
    const all = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns).map((column) => column.name);
    const hidden = this.hiddenSetFor(card);
    const visible = all.filter((name) => !hidden.has(name));
    const tableColumns = visible.length ? visible : all.slice(0, 1);
    this.dashboardItems = this.dashboardItems.map((item) => (
      item.id === card.item.id
        ? { ...item, tableColumns, tableColumnOrder: all }
        : item
    ));
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.markDraft();
    this.updateDashboardConfig();
  }
  getTableDisplay(card: DashboardCard): {
    columns: DatasetColumn[];
    allColumns: DatasetColumn[];
    rows: Record<string, string>[];
  } {
    const style = this.normalizeStyle(card.chart.style);
    const dataset = this.getFilteredDatasetForCard(card);
    const limit = Math.max(1, Math.floor(style.tableRowLimit || 50));

    const allColumns = this.orderedColumnsFor(card, dataset.columns);
    const hidden = this.hiddenSetFor(card);
    const visible = allColumns.filter((c) => !hidden.has(c.name));
    const columns = visible.length ? visible : allColumns.slice(0, 1);

    let rows = dataset.rows.slice(0, limit);
    const sort = this.tableSort.get(card.item.id);
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => this.compareTableValues(a?.[sort.column], b?.[sort.column]) * dir);
    }

    return { columns, allColumns, rows };
  }

  private compareTableValues(a: unknown, b: unknown): number {
    const aNil = a === null || a === undefined || a === '';
    const bNil = b === null || b === undefined || b === '';
    if (aNil && bNil) return 0;
    if (aNil) return -1;
    if (bNil) return 1;
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  toggleTableColumnMenu(card: DashboardCard, event: MouseEvent): void {
    event.stopPropagation();
    this.openTableColumnMenu = this.openTableColumnMenu === card.item.id ? null : card.item.id;
  }

  isTableColumnMenuOpen(card: DashboardCard): boolean {
    return this.openTableColumnMenu === card.item.id;
  }

  isTableColumnVisible(card: DashboardCard, column: string): boolean {
    return !this.hiddenSetFor(card).has(column);
  }

  toggleTableColumnVisibility(card: DashboardCard, column: string, event?: Event): void {
    event?.stopPropagation();
    const hidden = this.hiddenSetFor(card);
    if (hidden.has(column)) {
      hidden.delete(column);
      this.persistTableState(card);
      return;
    }
    const all = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns);
    const visibleCount = all.filter((c) => !hidden.has(c.name)).length;
    if (visibleCount <= 1) return; // keep at least one column
    hidden.add(column);
    this.persistTableState(card);
  }

  removeTableColumn(card: DashboardCard, column: string, event: MouseEvent): void {
    event.stopPropagation();
    const hidden = this.hiddenSetFor(card);
    const all = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns);
    const visibleCount = all.filter((c) => !hidden.has(c.name)).length;
    if (visibleCount <= 1) return;
    hidden.add(column);
    this.persistTableState(card);
    const sort = this.tableSort.get(card.item.id);
    if (sort?.column === column) this.tableSort.delete(card.item.id);
  }

  private moveTableColumn(card: DashboardCard, column: string, dir: -1 | 1): void {
    const all = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns);
    const names = all.map((c) => c.name);
    const idx = names.indexOf(column);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= names.length) return;
    [names[idx], names[target]] = [names[target], names[idx]];
    this.tableColumnOrder.set(card.item.id, names);
    this.persistTableState(card);
  }

  moveTableColumnLeft(card: DashboardCard, column: string, event: MouseEvent): void {
    event.stopPropagation();
    this.moveTableColumn(card, column, -1);
  }

  moveTableColumnRight(card: DashboardCard, column: string, event: MouseEvent): void {
    event.stopPropagation();
    this.moveTableColumn(card, column, 1);
  }

  canMoveTableColumnLeft(card: DashboardCard, column: string): boolean {
    const names = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns).map((c) => c.name);
    return names.indexOf(column) > 0;
  }

  canMoveTableColumnRight(card: DashboardCard, column: string): boolean {
    const names = this.orderedColumnsFor(card, this.getFilteredDatasetForCard(card).columns).map((c) => c.name);
    const idx = names.indexOf(column);
    return idx >= 0 && idx < names.length - 1;
  }

  sortTableColumn(card: DashboardCard, column: string, event: MouseEvent): void {
    event.stopPropagation();
    const current = this.tableSort.get(card.item.id);
    if (!current || current.column !== column) {
      this.tableSort.set(card.item.id, { column, dir: 'asc' });
    } else if (current.dir === 'asc') {
      this.tableSort.set(card.item.id, { column, dir: 'desc' });
    } else {
      this.tableSort.delete(card.item.id);
    }
  }

  tableSortIndicator(card: DashboardCard, column: string): string {
    const sort = this.tableSort.get(card.item.id);
    if (!sort || sort.column !== column) return '';
    return sort.dir === 'asc' ? '▲' : '▼';
  }

  resetTableColumns(card: DashboardCard, event?: MouseEvent): void {
    event?.stopPropagation();
    this.tableHiddenColumns.delete(card.item.id);
    this.tableColumnOrder.delete(card.item.id);
    this.tableSort.delete(card.item.id);
    this.dashboardItems = this.dashboardItems.map((item) => (
      item.id === card.item.id
        ? { ...item, tableColumns: undefined, tableColumnOrder: undefined }
        : item
    ));
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.markDraft();
    this.updateDashboardConfig();
    this.openTableColumnMenu = null;
  }

  /** Close any open data-table Columns dropdown on an outside click. */
  @HostListener('document:click')
  onDocumentClickCloseTableMenu(): void {
    if (this.openTableColumnMenu) this.openTableColumnMenu = null;
  }

  // ── Grid render + cross-filtering ───────────────────────────────────────────
  private renderDashboardDraft(): void {
    const refs = this.dashboardChartRefs?.toArray() || [];
    const cards = this.dashboardCards;
    const echartsCards = this.eChartsDashboardCards;
    const visibleItemIds = new Set(cards.map((card) => card.item.id));

    this.dashboardCharts.forEach((chart, itemId) => {
      if (!visibleItemIds.has(itemId)) {
        chart.dispose();
        this.dashboardCharts.delete(itemId);
      }
    });

    refs.forEach((ref, index) => {
      const card = echartsCards[index];
      if (!card) {
        return;
      }

      let chart = this.dashboardCharts.get(card.item.id);
      if (chart && chart.getDom() !== ref.nativeElement) {
        chart.dispose();
        this.dashboardCharts.delete(card.item.id);
        chart = undefined;
      }

      chart = chart || echarts.init(ref.nativeElement);
      this.dashboardCharts.set(card.item.id, chart);

      const dataset = this.getFilteredDatasetForCard(card);
      const style = this.normalizeStyle(card.chart.style);
      const titleText = style.titleOverride || card.chart.name || '';

      this.currentShapeOptions = this.persistence.getShapeOptionsFromChart(card.chart);
      let option = this.chartOptionBuilder.buildChartOption(
        card.chart.chartType,
        card.chart.mapping,
        card.chart.aggregation || 'sum',
        dataset,
        style,
        card.chart.seriesField || '',
        titleText,
        this.buildChartOptionContext(),
      );
      this.currentShapeOptions = null;

      // Preserve legend selections the user toggled (notMerge:true resets them).
      const existingOpt = chart.getOption() as echarts.EChartsOption;
      const existingLegendRaw = Array.isArray(existingOpt?.legend) ? existingOpt.legend[0] : existingOpt?.legend;
      const savedSelected = (existingLegendRaw as Record<string, unknown> | undefined)?.['selected'] as Record<string, boolean> | undefined;
      if (savedSelected && Object.keys(savedSelected).length) {
        if (Array.isArray(option.legend)) {
          const [first, ...rest] = option.legend as echarts.LegendComponentOption[];
          option = { ...option, legend: [{ ...first, selected: savedSelected }, ...rest] };
        } else if (option.legend) {
          option = { ...option, legend: { ...(option.legend as echarts.LegendComponentOption), selected: savedSelected } };
        }
      }

      chart.setOption(
        option,
        true,
      );
      chart.resize();

      chart.off('click');
      chart.on('click', (params: unknown) => this.onDashboardChartClick(card, params as { name?: string }));
    });
  }

  private onDashboardChartClick(sourceCard: DashboardCard, params: { name?: string }): void {
    const filterKey = this.getEffectiveFilterKey(sourceCard);
    if (!filterKey) return;
    const filterValue = String(params?.name ?? '').trim();
    if (!filterValue) return;
    this.toggleCrossFilterValue(sourceCard, filterKey, filterValue);
  }

  onTableRowClick(sourceCard: DashboardCard, row: Record<string, string>): void {
    const filterKey = this.getEffectiveFilterKey(sourceCard);
    if (!filterKey) return;
    const raw = row[filterKey];
    if (raw === undefined || raw === null || String(raw).trim() === '') return;
    this.toggleCrossFilterValue(sourceCard, filterKey, String(raw));
  }

  /**
   * Drill-down row click in the arrange view. Unlike a flat table (one publish
   * column), a drill-down row lives at a specific LEVEL, so the emitted event
   * carries the clicked level's own column + value. We filter listeners by THAT
   * column, falling back to the card's configured filterKey only if absent.
   * `null` = row toggled off → clear this source's filter.
   */
  onDrillDownRowClick(
    sourceCard: DashboardCard,
    event: { column: string; value: string } | null,
  ): void {
    if (!event) {
      this.clearCrossFilterFromSource(sourceCard.chart.id);
      return;
    }
    const filterKey = event.column || this.getEffectiveFilterKey(sourceCard);
    if (!filterKey) return;
    if (event.value === undefined || event.value === null || String(event.value).trim() === '') return;
    this.toggleCrossFilterValue(sourceCard, filterKey, String(event.value));
  }

  private toggleCrossFilterValue(sourceCard: DashboardCard, filterKey: string, value: string): void {
    const sourceChartId = sourceCard.chart.id;
    const existing = this.activeCrossFilters.get(sourceChartId);
    if (existing && existing.filterKey === filterKey) {
      const idx = existing.filterValues.indexOf(value);
      if (idx >= 0) {
        existing.filterValues.splice(idx, 1);
        if (existing.filterValues.length === 0) {
          this.activeCrossFilters.delete(sourceChartId);
        }
      } else {
        existing.filterValues.push(value);
      }
    } else {
      this.activeCrossFilters.set(sourceChartId, {
        sourceChartId,
        sourceItemId: sourceCard.item.id,
        filterKey,
        filterValues: [value],
      });
    }
    this.scheduleDashboardRender();
  }

  clearCrossFilterFromSource(sourceChartId: string): void {
    this.activeCrossFilters.delete(sourceChartId);
    this.scheduleDashboardRender();
  }

  clearCrossFilterValue(sourceChartId: string, value: string): void {
    const filter = this.activeCrossFilters.get(sourceChartId);
    if (!filter) return;
    const idx = filter.filterValues.indexOf(value);
    if (idx < 0) return;
    filter.filterValues.splice(idx, 1);
    if (filter.filterValues.length === 0) {
      this.activeCrossFilters.delete(sourceChartId);
    }
    this.scheduleDashboardRender();
  }

  clearAllCrossFilters(): void {
    this.activeCrossFilters.clear();
    this.scheduleDashboardRender();
  }

  getEffectiveFilterKey(card: DashboardCard): string {
    return (card.item.filterKeyOverride && card.item.filterKeyOverride.trim())
      || card.chart.filterKey
      || '';
  }

  getListenerCardItemIds(sourceCard: DashboardCard): string[] {
    if (Array.isArray(sourceCard.item.listenerItemIds)) {
      return sourceCard.item.listenerItemIds;
    }
    const sourceListenerWidgetIds = new Set(
      this.persistence.mergeListenerWidgetIds(
        sourceCard.chart.filterListener || [],
        sourceCard.chart.filterListenerCustom,
        this.persistence.buildWidgetId(sourceCard.chart.id),
      ),
    );
    return this.dashboardCards
      .filter((c) => c.item.id !== sourceCard.item.id
        && sourceListenerWidgetIds.has(this.persistence.buildWidgetId(c.chart.id)))
      .map((c) => c.item.id);
  }

  getActiveFiltersForCard(card: DashboardCard): Array<{
    sourceChartId: string;
    sourceLabel: string;
    filterKey: string;
    values: string[];
    isSelf: boolean;
  }> {
    const result: Array<{
      sourceChartId: string;
      sourceLabel: string;
      filterKey: string;
      values: string[];
      isSelf: boolean;
    }> = [];

    for (const filter of this.activeCrossFilters.values()) {
      const isSelf = filter.sourceChartId === card.chart.id && filter.sourceItemId === card.item.id;
      if (isSelf) {
        result.push({
          sourceChartId: filter.sourceChartId,
          sourceLabel: 'This component',
          filterKey: filter.filterKey,
          values: [...filter.filterValues],
          isSelf: true,
        });
        continue;
      }

      const sourceCard = this.dashboardCards.find((c) => c.chart.id === filter.sourceChartId);
      if (!sourceCard) continue;
      const listeners = this.getListenerCardItemIds(sourceCard);
      if (!listeners.includes(card.item.id)) continue;
      if (!card.chart.dataset.columns.some((col) => col.name === filter.filterKey)) continue;

      result.push({
        sourceChartId: filter.sourceChartId,
        sourceLabel: sourceCard.chart.name,
        filterKey: filter.filterKey,
        values: [...filter.filterValues],
        isSelf: false,
      });
    }
    return result;
  }

  toggleListenerEditor(itemId: string): void {
    this.expandedListenerEditor = this.expandedListenerEditor === itemId ? null : itemId;
  }

  setItemFilterKey(item: DashboardDraftItem, value: string): void {
    const trimmed = (value || '').trim();
    this.dashboardItems = this.dashboardItems.map((current) =>
      current.id === item.id ? { ...current, filterKeyOverride: trimmed || undefined } : current,
    );
    this.activeCrossFilters.delete(item.chartId);
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  isItemListener(sourceItem: DashboardDraftItem, candidateItemId: string): boolean {
    const sourceCard = this.dashboardCards.find((c) => c.item.id === sourceItem.id);
    if (!sourceCard) return false;
    return this.getListenerCardItemIds(sourceCard).includes(candidateItemId);
  }

  toggleItemListener(sourceItem: DashboardDraftItem, candidateItemId: string): void {
    const sourceCard = this.dashboardCards.find((c) => c.item.id === sourceItem.id);
    if (!sourceCard) return;
    const currentListeners = this.getListenerCardItemIds(sourceCard);
    const isListener = currentListeners.includes(candidateItemId);
    const next = isListener
      ? currentListeners.filter((id) => id !== candidateItemId)
      : [...currentListeners, candidateItemId];
    this.dashboardItems = this.dashboardItems.map((current) =>
      current.id === sourceItem.id ? { ...current, listenerItemIds: next } : current,
    );
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  resetItemListeners(sourceItem: DashboardDraftItem): void {
    this.dashboardItems = this.dashboardItems.map((current) =>
      current.id === sourceItem.id
        ? { ...current, listenerItemIds: undefined, filterKeyOverride: undefined }
        : current,
    );
    this.activeCrossFilters.delete(sourceItem.chartId);
    this.persistence.persistDashboardDraft(this.dashboardItems);
    this.markDraft();
    this.updateDashboardConfig();
    this.scheduleDashboardRender();
  }

  /** A compact signature of every cross-filter + global-filter that narrows this
   *  card, used to bust the drill-down card-data cache when filters change. */
  private crossFilterSignatureFor(card: DashboardCard): string {
    const parts: string[] = [];
    for (const filter of this.activeCrossFilters.values()) {
      if (filter.sourceChartId === card.chart.id && filter.sourceItemId === card.item.id) continue;
      const sourceCard = this.dashboardCards.find((c) => c.chart.id === filter.sourceChartId);
      if (!sourceCard) continue;
      if (!this.getListenerCardItemIds(sourceCard).includes(card.item.id)) continue;
      if (!card.chart.dataset.columns.some((col) => col.name === filter.filterKey)) continue;
      parts.push(`${filter.filterKey}=${[...filter.filterValues].sort().join(',')}`);
    }
    for (const filter of this.dashboardGlobalFilters) {
      const selected = this.activeGlobalFilters[filter.key];
      if (!selected || selected.length === 0) continue;
      if (!card.chart.dataset.columns.some((col) => col.name === filter.key)) continue;
      parts.push(`g:${filter.key}=${[...selected].sort().join(',')}`);
    }
    return parts.sort().join('|');
  }

  private getFilteredDatasetForCard(card: DashboardCard): UploadedDataset {
    let dataset = card.chart.dataset;

    for (const filter of this.activeCrossFilters.values()) {
      if (filter.sourceChartId === card.chart.id && filter.sourceItemId === card.item.id) {
        continue;
      }

      const sourceCard = this.dashboardCards.find((c) => c.chart.id === filter.sourceChartId);
      if (!sourceCard) continue;

      const listeners = this.getListenerCardItemIds(sourceCard);
      if (!listeners.includes(card.item.id)) continue;
      if (!dataset.columns.some((col) => col.name === filter.filterKey)) continue;

      dataset = {
        ...dataset,
        rows: dataset.rows.filter((row) => filter.filterValues.includes(row[filter.filterKey])),
      };
    }

    for (const filter of this.dashboardGlobalFilters) {
      const selected = this.activeGlobalFilters[filter.key];
      if (!selected || selected.length === 0) continue;
      if (!dataset.columns.some((column) => column.name === filter.key)) continue;
      dataset = {
        ...dataset,
        rows: dataset.rows.filter((row) => selected.includes(row[filter.key])),
      };
    }

    return dataset;
  }

  refreshVisibleCharts(): void {
    setTimeout(() => {
      this.dashboardCharts.forEach((chart) => chart.resize());
      this.scheduleDashboardRender();
    }, 0);
  }
  private scheduleDashboardRender(): void {
    if (this.dashboardRenderTimer) {
      clearTimeout(this.dashboardRenderTimer);
    }

    this.dashboardRenderTimer = setTimeout(() => {
      requestAnimationFrame(() => this.renderDashboardDraft());
    }, 50);
  }

  // ── Stepper navigation ──────────────────────────────────────────────────────
  // Mat-stepper keeps all step contents mounted (just hides inactive ones).
  // When the user navigates to the Arrange step, the chart <div> elements were
  // previously hidden with 0×0 dimensions, so ECharts couldn't measure them.
  // The QueryList.changes subscription doesn't fire because the refs already
  // existed. So when we arrive on Arrange (last step), force-resize existing
  // chart instances and re-render. We also wait past mat-stepper's animation
  // (~225ms) before measuring.
  onDashboardStepChange(selectedIndex: number): void {
    // Arrange is the 4th step (index 3). Only act when entering it.
    if (selectedIndex !== 3) {
      return;
    }
    setTimeout(() => {
      this.dashboardCharts.forEach((chart) => chart.resize());
      this.renderDashboardDraft();
    }, 260);
  }

  showDashboardPreviewStep(): void {
    setTimeout(() => {
      if (this.dashboardStepper) {
        this.dashboardStepper.selectedIndex = 3;
      } else {
        setTimeout(() => this.showDashboardPreviewStep(), 0);
        return;
      }
      this.onDashboardStepChange(3);
    });
  }

  showDashboardSetupStep(): void {
    setTimeout(() => {
      if (this.dashboardStepper) {
        this.dashboardStepper.selectedIndex = 0;
      } else {
        setTimeout(() => this.showDashboardSetupStep(), 0);
      }
    });
  }

  showDashboardComponentsStep(): void {
    setTimeout(() => {
      if (this.dashboardStepper) {
        this.dashboardStepper.selectedIndex = 1;
      } else {
        setTimeout(() => this.showDashboardComponentsStep(), 0);
      }
    });
  }

  focusDashboardFilterBuilder(): void {
    this.showDashboardPreviewStep();
    setTimeout(() => {
      const input = this.dashboardFilterLabelInput?.nativeElement;
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus();
    }, 80);
  }

  showDashboardFiltersStep(): void {
    setTimeout(() => {
      if (this.dashboardStepper) {
        this.dashboardStepper.selectedIndex = 2;
      } else {
        setTimeout(() => this.showDashboardFiltersStep(), 0);
      }
    });
  }

  private updateDashboardConfig(): void {
    const config = this.persistence.buildDashboardConfig();
    this.generatedDashboardConfig = this.dashboardItems.length
      ? JSON.stringify(config, null, 2)
      : '';
  }

  /** Ask the shell to switch the active view. */
  setViewMode(mode: 'data' | 'charts' | 'dashboards'): void {
    this.viewModeChange.emit(mode);
  }
}







